import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { FileText, Plus, Loader2, X, ExternalLink, Upload, Link2, Paperclip } from 'lucide-react';
import { projectAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';

// Liste tenue à la main ici (elle reprenait à l'origine l'onglet LISTES du classeur). Le type est
// un simple texte enregistré avec chaque document : retirer ou renommer un type ici ne touche pas
// les documents déjà enregistrés — un renommage se reporte par une migration, comme « Devis » ->
// « Devis signé » (2026-10-23, 5 documents). Retirés à la demande de Samuel : « Facture » et « DTU ».
const DOC_TYPES = ['Devis signé', 'Prise métrés', 'Plan', 'Fiche technique', 'Compte rendu', 'Mémoire technique', 'Autre'];

const EMPTY_FORM = { type: DOC_TYPES[0], name: '', link: '', comment: '' };
const MAX_UPLOAD_BYTES = 25 * 1024 * 1024; // 25 Mo, aligné sur la limite serveur

const DocumentDialog = ({ project, isOpen, onOpenChange }) => {
  const { toast } = useToast();
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  // « lien » (coller un lien Drive déjà existant) ou « fichier » (l'app
  // héberge le fichier elle-même, via GridFS — pas Drive, un compte de
  // service n'a pas de quota de stockage pour y créer des fichiers)
  const [mode, setMode] = useState('file');
  const [selectedFile, setSelectedFile] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const loadDocuments = useCallback(async () => {
    if (!project) return;
    setIsLoading(true);
    try {
      const result = await projectAPI.getDocuments(project.id);
      setDocuments(result.documents || []);
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les documents',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [project, toast]);

  useEffect(() => {
    if (isOpen && project) {
      setForm(EMPTY_FORM);
      setSelectedFile(null);
      setMode('file');
      loadDocuments();
    }
    // eslint-disable-next-line
  }, [isOpen, project?.id]);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0] || null;
    if (f && f.size > MAX_UPLOAD_BYTES) {
      toast({
        title: 'Fichier trop volumineux',
        description: `${f.name} dépasse 25 Mo — utilisez un lien Drive existant à la place.`,
        variant: 'destructive',
        duration: 6000,
      });
      e.target.value = '';
      setSelectedFile(null);
      return;
    }
    setSelectedFile(f);
    // Le nom du document se pré-remplit sur le nom du fichier, modifiable ensuite
    if (f && !form.name.trim()) setForm((s) => ({ ...s, name: f.name }));
  };

  const canSave = mode === 'file' ? !!selectedFile : !!form.name.trim();

  const handleSave = async () => {
    if (!canSave) return;
    setIsSaving(true);
    try {
      if (mode === 'file') {
        await projectAPI.uploadDocument(project.id, { ...form, file: selectedFile });
        toast({
          title: 'Document envoyé',
          description: `${project.name} — ${form.type} : ${form.name || selectedFile.name}`,
          duration: 4000,
        });
      } else {
        await projectAPI.addDocument(project.id, form);
        toast({
          title: 'Document enregistré',
          description: `${project.name} — ${form.type} : ${form.name}`,
          duration: 4000,
        });
      }
      setForm(EMPTY_FORM);
      setSelectedFile(null);
      await loadDocuments();
    } catch (error) {
      toast({
        title: "Échec de l'enregistrement",
        description: error?.response?.data?.detail || String(error),
        variant: 'destructive',
        duration: 6000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (doc) => {
    setDeletingId(doc.id);
    try {
      await projectAPI.deleteDocument(project.id, doc.id);
      toast({
        title: 'Document supprimé',
        description: `${doc.type || 'Document'} — ${doc.name}`,
        duration: 3000,
      });
      await loadDocuments();
    } catch (error) {
      toast({
        title: 'Échec de la suppression',
        description: error?.response?.data?.detail || String(error),
        variant: 'destructive',
        duration: 5000,
      });
      await loadDocuments();
    } finally {
      setDeletingId(null);
    }
  };

  if (!project) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl bg-white border border-blue-100 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center text-blue-800">
            <FileText className="w-5 h-5 mr-2" />
            Documents — {project.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Documents déjà enregistrés */}
          <div>
            <Label className="text-blue-700">Documents déjà enregistrés</Label>
            {isLoading ? (
              <p className="text-sm text-gray-500 mt-2 flex items-center">
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Chargement...
              </p>
            ) : documents.length === 0 ? (
              <p className="text-sm text-gray-500 mt-2">Aucun document enregistré pour ce chantier.</p>
            ) : (
              <div className="mt-2 border border-blue-100 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-blue-50 text-left text-xs text-blue-700">
                      <th className="px-3 py-2 font-medium">Date</th>
                      <th className="px-3 py-2 font-medium">Type</th>
                      <th className="px-3 py-2 font-medium">Nom</th>
                      <th className="px-3 py-2 font-medium">Statut</th>
                      <th className="px-3 py-2 font-medium">Commentaire</th>
                      <th className="px-2 py-2 w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {documents.map((d) => (
                      <tr key={d.id} className="group border-t border-blue-50 hover:bg-blue-50/50">
                        <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{d.date || '—'}</td>
                        <td className="px-3 py-2 text-gray-700">{d.type || '—'}</td>
                        <td className="px-3 py-2 text-gray-900 font-medium">
                          {d.link ? (
                            <a href={d.link} target="_blank" rel="noopener noreferrer"
                               className="inline-flex items-center text-blue-700 hover:underline">
                              {d.name}
                              <ExternalLink className="w-3 h-3 ml-1 shrink-0" />
                            </a>
                          ) : d.name}
                        </td>
                        <td className="px-3 py-2 text-gray-600">{d.status || '—'}</td>
                        <td className="px-3 py-2 text-gray-500">{d.comment || '—'}</td>
                        <td className="px-2 py-2">
                          <button
                            onClick={() => handleDelete(d)}
                            disabled={deletingId === d.id}
                            className="hidden group-hover:flex items-center justify-center text-red-500 hover:text-red-700 disabled:opacity-50"
                            title="Supprimer ce document"
                          >
                            {deletingId === d.id
                              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              : <X className="w-3.5 h-3.5" />}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Ajouter un document */}
          <div className="bg-blue-50 p-4 rounded-lg space-y-3">
            <h3 className="text-sm font-medium text-blue-800">Ajouter un document</h3>

            {/* Fichier envoyé par l'app (hébergé par elle) ou lien vers un
                fichier déjà présent dans Drive */}
            <div className="flex rounded-md border border-blue-200 overflow-hidden text-sm">
              <button type="button" onClick={() => setMode('file')}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 ${
                        mode === 'file' ? 'bg-blue-600 text-white' : 'bg-white text-blue-700 hover:bg-blue-50'}`}>
                <Upload className="w-3.5 h-3.5" /> Envoyer un fichier
              </button>
              <button type="button" onClick={() => setMode('link')}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 border-l border-blue-200 ${
                        mode === 'link' ? 'bg-blue-600 text-white' : 'bg-white text-blue-700 hover:bg-blue-50'}`}>
                <Link2 className="w-3.5 h-3.5" /> Coller un lien Drive
              </button>
            </div>

            {mode === 'file' ? (
              <div className="space-y-1">
                <Label htmlFor="doc-file" className="text-blue-700 text-xs">Fichier (25 Mo max)</Label>
                <label htmlFor="doc-file"
                       className="flex items-center gap-2 border border-dashed border-blue-300 rounded-md px-3 py-2 text-sm cursor-pointer bg-white hover:bg-blue-50">
                  <Paperclip className="w-4 h-4 text-blue-500 shrink-0" />
                  <span className="truncate text-gray-700">
                    {selectedFile ? selectedFile.name : 'Choisir un fichier...'}
                  </span>
                  {selectedFile && (
                    <span className="text-xs text-gray-400 shrink-0">
                      {(selectedFile.size / 1024 / 1024).toFixed(1)} Mo
                    </span>
                  )}
                </label>
                <input id="doc-file" type="file" onChange={handleFileChange} className="hidden" />
                <p className="text-xs text-blue-600">
                  Hébergé par l'application — le lien n'est accessible que depuis un appareil qui peut atteindre ce PC (comme l'app elle-même).
                </p>
              </div>
            ) : (
              <div className="space-y-1">
                <Label htmlFor="doc-link" className="text-blue-700 text-xs">Lien Drive</Label>
                <Input id="doc-link" value={form.link} placeholder="https://drive.google.com/..."
                       onChange={(e) => setForm((f) => ({ ...f, link: e.target.value }))}
                       className="border-blue-200" />
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="doc-type" className="text-blue-700 text-xs">Type</Label>
                <select
                  id="doc-type"
                  value={form.type}
                  onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
                  className="w-full h-9 px-3 border border-blue-200 rounded-md text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
                >
                  {DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="doc-name" className="text-blue-700 text-xs">
                  Nom du document{mode === 'file' && ' (optionnel)'}
                </Label>
                <Input id="doc-name" value={form.name}
                       placeholder={mode === 'file' ? 'Par défaut : nom du fichier' : 'Devis signé, plan RDC...'}
                       onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                       className="border-blue-200" />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="doc-comment" className="text-blue-700 text-xs">Commentaire (optionnel)</Label>
              <Textarea id="doc-comment" value={form.comment} rows={2}
                        onChange={(e) => setForm((f) => ({ ...f, comment: e.target.value }))}
                        className="border-blue-200" />
            </div>
            <Button
              onClick={handleSave}
              disabled={!canSave || isSaving}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              {isSaving
                ? (mode === 'file' ? 'Envoi en cours...' : 'Enregistrement...')
                : 'Ajouter ce document'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentDialog;
